/* eslint-disable indent */
import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router';
import { Questionnaire, QuestionnaireSkill } from '../../../models/questionnaire';
import { useAppActions, useAppState } from '../../../overmind';
import { QuestionnaireWidget } from '../../../widgets';
import { FetchType } from '../../../enums';
import { ExplainerFocusPanelLayout } from '../../../widgets/layouts/focus-panel-layouts/explainer-focus-panel/explainer-focus-panel.layout';
import { PagePath } from '../../../navigation/navigation.enums';
import { PageLoaderLayer } from '../../../components/general/loading-state/loader-layers/page-loader-layer/page-loader-layer';
import { PageHeader, Panel, capitalCase } from '@keplerco/core';
import { useAnalysisBasePagePath } from '../use-analysis-base-page-path';
import { useKeplerNavigate } from '../../../navigation/guards/use-kepler-navigate';

export function QuestionnairePage(): JSX.Element {
  const { questionnaireSlug } = useParams<any>();
  const keplerNavigate = useKeplerNavigate();
  const basePagePath = useAnalysisBasePagePath();

  const actions = useAppActions();
  const { forcedAssessments, activeAssessmentConfig } = useAppState();

  const [questionnaire, setQuestionnaire] = useState<Questionnaire>();
  const [queriedSkill, setQueriedSkill] = useState<QuestionnaireSkill>();

  useEffect(() => {
    async function getData() {
      actions.startLoader({ path: PagePath.analysisQuestionnaire, type: FetchType.PageFetching });
      const data = await actions.getQuestionnaires(questionnaireSlug!);
      setQuestionnaire(data);
      actions.stopLoader(PagePath.analysisQuestionnaire);
    }

    if (!questionnaire) getData();
  });

  return (
    <div style={{ minHeight: `calc(90vh - 75px)`, width: '100%' }}>
      <PageLoaderLayer path={PagePath.analysisQuestionnaire}>
        <div className="wrapper stack">
          <PageHeader
            breadcrumbs={
              !!forcedAssessments.length
                ? [
                    {
                      name: 'Pending assessments',
                      url: PagePath.analysisBase + PagePath.analysisPendingAssessments,
                    },
                    {
                      name: activeAssessmentConfig?.assessmentName ?? 'Assessment',
                      url: basePagePath,
                    },
                    {
                      name: 'Questionnaires',
                      url: basePagePath + PagePath.analysisQuestionnaires,
                    },
                    {
                      name: questionnaire?.name ?? 'Questionnaire',
                      url: location.pathname,
                    },
                  ]
                : [
                    {
                      name: activeAssessmentConfig?.assessmentName ?? 'Assessment',
                      url: basePagePath,
                    },
                    {
                      name: 'Questionnaires',
                      url: basePagePath + PagePath.analysisQuestionnaires,
                    },
                    {
                      name: questionnaire?.name ?? 'Questionnaire',
                      url: location.pathname,
                    },
                  ]
            }
            title={questionnaire?.name ?? 'Questionnaire'}
            subtitle={questionnaire?.description}
            hideContentOnMobile
          />

          <br />

          {!!questionnaire && (
            <QuestionnaireWidget
              {...questionnaire}
              onSkillQuery={skill => setQueriedSkill(skill)}
              onCompleteAll={async () => {
                actions.startLoader({ path: PagePath.analysisQuestionnaire, type: FetchType.Sending });
                const response = await actions.getAssessmentConfig(activeAssessmentConfig!.assessmentSlug!);
                if (response?.assessmentResultReady) {
                  await actions.getForcedAssessments();
                  actions.clearAssessmentConfig();
                } else {
                  keplerNavigate(basePagePath + PagePath.analysisQuestionnaires);
                }
                actions.stopLoader(PagePath.analysisQuestionnaire);
              }}
            />
          )}
        </div>
      </PageLoaderLayer>

      <Panel open={!!queriedSkill} onClose={() => setQueriedSkill(void 0)}>
        {!!queriedSkill && (
          <ExplainerFocusPanelLayout title={capitalCase(queriedSkill.skillName)} onClose={() => setQueriedSkill(void 0)}>
            {queriedSkill.skillDescription}
          </ExplainerFocusPanelLayout>
        )}
      </Panel>
    </div>
  );
}
